import { useMemo, useState } from "react";

import { Network, Target } from "lucide-react";

import { PartsForceGraph } from "~/components/parts-force-graph";
import {
    SegmentedTabs,
    SegmentedTabsItem,
} from "~/components/ui/segmented-tabs";
import { Switch } from "~/components/ui/switch";
import { H2 } from "~/components/ui/typography";
import { useFeatureFlag } from "~/hooks/use-account-feature-flags";
import { cn } from "~/lib/utils";

import {
    type DpmpMapEdgeInput,
    type DpmpMapNodeInput,
} from "../lib/dpmp-parts-map-layout";
import { DpmpPartsMap } from "./dpmp-parts-map";

// AIDEV-NOTE: The scoped Parts Map — one SESSION's parts (session notes) or one PART and
// its direct relations (part detail). Michelle works session-by-session and folds each
// session map into a growing system map, so these smaller maps are where a client learns
// to read one at all; the whole-system map on /parts is the end of that path, not the
// start (Michelle Glass call, 2026-09-04).
//
// Both the DPMP® format and the existing Parts Companion force graph render the same
// scope, switched by SegmentedTabs — the in-repo primitive for "two views of the same
// content". DPMP® is the default when the add-on is on, otherwise the force graph is.

const ENABLE_DPMP_FLAG = "enable-dpmp";

export interface ScopedMapPart {
    id: string;
    name: string;
    type: string | null;
    image?: { signedUrl?: string; url?: string | null } | null;
}

export interface ScopedPartsMapProps {
    parts: ScopedMapPart[];
    edges: DpmpMapEdgeInput[];
    /** Parts the detector suspected but that were never named aloud. Practitioners only. */
    suspectedParts?: ScopedMapPart[];
    /** Practitioner surfaces may offer the suspected-parts toggle; client surfaces never. */
    allowSuspectedToggle?: boolean;
    onSelectPart?: (partId: string) => void;
    className?: string;
    /** Fixed size in px; measured from the container when omitted. */
    size?: number;
    ariaLabel: string;
    /**
     * "part" tightens the map for a single part's neighbourhood: smaller avatars and
     * shorter names. BOTH variants draw real lines between connected parts — at this scope
     * there are few enough to draw honestly, and a line says "these two" better than a stub
     * labelled with a name already shown on the part it points at.
     */
    variant?: "session" | "part";
    /** Rendered above the map, with the format toggle pushed to the opposite edge. */
    heading?: string;
    /**
     * Classes for the map surface itself.
     *
     * AIDEV-NOTE: The map draws its own white rounded card and casts its own shadow — the
     * shadow lives on the container rather than inside the SVG, where a viewBox would clip
     * it. That is right when the map IS the card (session notes), and doubles up when it
     * is dropped inside one (part detail), which is what this is for.
     */
    mapClassName?: string;
}

type MapFormat = "dpmp" | "pc";

function toNodeInput(part: ScopedMapPart): DpmpMapNodeInput {
    return {
        id: part.id,
        name: part.name,
        type: part.type,
        imageUrl: part.image?.signedUrl ?? part.image?.url ?? null,
    };
}

export function ScopedPartsMap({
    parts,
    edges,
    suspectedParts = [],
    allowSuspectedToggle = false,
    onSelectPart,
    className,
    size,
    ariaLabel,
    variant = "session",
    heading,
    mapClassName,
}: ScopedPartsMapProps) {
    const dpmpEnabled = useFeatureFlag(ENABLE_DPMP_FLAG);
    // AIDEV-NOTE: null = "user has not chosen", so the flag decides. useFeatureFlag
    // resolves AFTER mount, so seeding useState from it froze the default at whatever the
    // flag was during the first render — a member could land on Relationships and never be
    // shown the format they enabled the add-on for.
    const [chosenFormat, setChosenFormat] = useState<MapFormat | null>(null);
    // AIDEV-NOTE: The flag decided the DEFAULT TAB but the DPMP® tab rendered for everyone,
    // so an account without the add-on could switch to Michelle Glass's format and see it —
    // a new practitioner met it in their onboarding demo (Adam, 2026-09-09). The flag also
    // resolves asynchronously, so `format` can hold "dpmp" from a mount that briefly saw it
    // true; deriving the effective format rather than trusting state closes both. Without
    // the add-on there is only one format, so the toggle itself has nothing to offer.
    const effectiveFormat: MapFormat = dpmpEnabled
        ? chosenFormat ?? "dpmp"
        : "pc";
    const [showSuspected, setShowSuspected] = useState(false);

    const canToggleSuspected =
        allowSuspectedToggle && suspectedParts.length > 0;

    const nodes = useMemo(() => {
        // AIDEV-NOTE: A part with a missing/blank name crashed layout's
        // wrapPartName during session-page SSR ("Loading application...").
        // A nameless node cannot be drawn meaningfully — drop it.
        const named = (part: ScopedMapPart) =>
            typeof part.name === "string" && part.name.trim().length > 0;
        const base = parts.filter(named).map(toNodeInput);
        if (!canToggleSuspected || !showSuspected) return base;
        return [...base, ...suspectedParts.filter(named).map(toNodeInput)];
    }, [parts, suspectedParts, canToggleSuspected, showSuspected]);

    // AIDEV-NOTE: The force graph takes interaction-level rows; these edges are already
    // collapsed to part level, so the interaction ids are synthesised from the pair. They
    // are only used as React keys there, never dereferenced.
    // Edges must only reference surviving nodes — a nameless part is dropped
    // above, and D3 fails on a link whose endpoint node is missing.
    const relationships = useMemo(() => {
        const nodeIds = new Set(nodes.map((node) => node.id));
        return edges
            .filter(
                (edge) =>
                    nodeIds.has(edge.sourcePartId) &&
                    nodeIds.has(edge.targetPartId),
            )
            .map((edge) => ({
                id: `${edge.sourcePartId}:${edge.targetPartId}:${edge.type}`,
                sourcePartId: edge.sourcePartId,
                targetPartId: edge.targetPartId,
                type: edge.type,
                description: "",
                sourcePartInteractionId: edge.sourcePartId,
                targetPartInteractionId: edge.targetPartId,
            }));
    }, [edges, nodes]);

    if (parts.length === 0) return null;

    return (
        <div className={cn("flex flex-col gap-3", className)}>
            {/* AIDEV-NOTE: Heading owns the left edge; the format toggle is pushed to the
                far right so the row reads as a titled section rather than a control strip.
                Too narrow for both, and they stack — the toggle then lines up UNDER the
                heading on the same left edge rather than drifting to the far right on a
                line of its own, which read as unattached to anything. */}
            <div className="flex flex-col items-start gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
                {heading ? (
                    <H2 className="mb-0 pb-0 text-left">{heading}</H2>
                ) : null}
                {dpmpEnabled ? (
                    <SegmentedTabs
                        className={heading ? "sm:ml-auto" : undefined}
                        tone="crafted"
                        size="sm"
                        kind="iconText"
                        columns="2"
                        value={effectiveFormat}
                        onValueChange={(value) => {
                            if (value) setChosenFormat(value as MapFormat);
                        }}
                        aria-label={ariaLabel}
                    >
                        {/* AIDEV-NOTE: `Target` is three true concentric circles and no
                        crosshair — the DPMP® map's own three zones, exiles innermost. */}
                        <SegmentedTabsItem
                            value="dpmp"
                            icon={<Target className="size-4" />}
                            label="DPMP®"
                        />
                        <SegmentedTabsItem
                            value="pc"
                            icon={<Network className="size-4" />}
                            label="Relationships"
                        />
                    </SegmentedTabs>
                ) : null}

                {/* AIDEV-NOTE: Practitioner-only, and never on the whole-system map. Parts
                    the detector suspected but nobody named are exactly what a practitioner
                    scans for after a session, and exactly what overwhelms a client who does
                    not recognise them (Michelle Glass call, 2026-09-04). */}
                {canToggleSuspected ? (
                    <label className="flex items-center gap-2 text-sm text-brand-forest-700">
                        <Switch
                            checked={showSuspected}
                            onCheckedChange={setShowSuspected}
                            aria-label="Show suspected parts"
                        />
                        Show suspected parts
                    </label>
                ) : null}
            </div>

            {effectiveFormat === "dpmp" ? (
                <DpmpPartsMap
                    className={mapClassName}
                    nodes={nodes}
                    edges={edges}
                    mode="detail"
                    size={size}
                    // AIDEV-NOTE: Both scoped maps run smaller than the whole-system
                    // map's 30. A session holds few enough parts that the avatars had
                    // room to grow to the cap, which on a phone left them oversized
                    // against the canvas and crowded the names between them.
                    maxNodeRadius={variant === "part" ? 15 : 22}
                    nameMaxChars={variant === "part" ? 10 : undefined}
                    context={variant}
                    connectors
                    display={{ showSpokes: false, showSpokeLabels: false }}
                    onSelectPart={onSelectPart}
                />
            ) : (
                <PartsForceGraph
                    parts={nodes.map((node) => ({
                        id: node.id,
                        name: node.name,
                        type: node.type,
                        // AIDEV-NOTE: Keep the normalized image shared by both map formats;
                        // dropping it here made images disappear only after switching to PC.
                        defaultImage: node.imageUrl
                            ? { signedUrl: node.imageUrl }
                            : null,
                    }))}
                    relationships={relationships}
                    onNodeClick={onSelectPart}
                    surface="scoped-parts-map"
                />
            )}
        </div>
    );
}
